import { useEffect, FC, useRef } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Paper, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import transactions from '../../../Models/ReportModels/Transactions';
import Landing from '../../../Models/LandingModels/Landing';
import { getLandingById } from '../../../Services/LondunarkerfiAPIService';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { FilterPayload } from './Filters/FilterDefinition';

interface TransactionsReportProps {
  apiRef: React.MutableRefObject<GridApiCommunity>;
  toggleDashboardOpen: (open: boolean) => void;
  onLandingSelect: (landing: Landing, newLandingId?: number, landings?: Landing[], newWeightNoteId?: number) => void;
  dateFrom: string;
  setDateFrom: (date: string) => void;
  dateTo: string;
  setDateTo: (date: string) => void;
  loading: boolean;
  transactions: transactions[];
  fetchTransactions: (
    pageToFetch: number,
    reset: boolean,
    filterModel?: FilterPayload,
    dateFrom?: string,
    dateTo?: string
  ) => Promise<void>;
  hasMore: boolean;
  setPage: (val: (prev: number) => number) => void;

}

const formatDate = (date: dayjs.Dayjs) => date.format('DD.MM.YYYY');

const TransactionsReport: FC<TransactionsReportProps> = (props) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const handleRowClick = async (row: transactions) => {
    const landing = await getLandingById(row.landingId);
    props.onLandingSelect(landing, landing.id, undefined, undefined);
    props.toggleDashboardOpen(false);
  };

  // Custom infinite scroll handler
  useEffect(() => {
    const gridNode = gridRef.current;
    if (!gridNode) return;

    const handleScroll = () => {
      if (props.loading || !props.hasMore) return;
      const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
      if (!gridWindow) return;
      const { scrollTop, scrollHeight, clientHeight } = gridWindow as HTMLElement;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        props.setPage(prev => prev + 1);
      }
    };

    const gridWindow = gridNode.querySelector('.MuiDataGrid-virtualScroller');
    if (gridWindow) {
      gridWindow.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (gridWindow) {
        gridWindow.removeEventListener('scroll', handleScroll);
      }
    };
  }, [props]);

  const columns: GridColDef[] = [
    {
      field: 'landingId',
      headerName: 'Löndun #',
      type: 'number',
      valueFormatter: (params) => String(params.value),
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'landingDate',
      headerName: 'Dagsetning',
      flex: 1,
      minWidth: 120,
      sortable: true,
      valueGetter: (params) => dayjs(params.row.landingDate).format('DD.MM.YYYY'),
      filterable: false,
    },
    {
      field: 'shipRegistrationNumber',
      headerName: 'Skipaskrárnúmer',
      type: 'number',
      valueFormatter: (params) => String(params.value),
      renderCell: (params) => <strong>{params.value}</strong>,
      flex: 1,
      minWidth: 80,
      sortable: true,
    },
    {
      field: 'shipName',
      headerName: 'Skip heiti',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'gear',
      headerName: 'Veiðarfæri',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'sellerNameSSN',
      headerName: 'Seljandi',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'buyerNameSSN',
      headerName: 'Kaupandi',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'receiverNameSSN',
      headerName: 'Viðtakandi',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'fishConditionName',
      headerName: 'Fisktegund',
      flex: 1,
      minWidth: 120,
      sortable: true,
    },
    {
      field: 'countFish',
      headerName: 'Magn',
      flex: 1,
      minWidth: 120,
      sortable: true,
      filterable: false,
      type: 'number',
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="is">
      <Paper elevation={3} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <DatePicker
              label="Frá"
              value={dayjs(props.dateFrom, 'DD.MM.YYYY')}
              onChange={date => {
                if (date) props.setDateFrom(formatDate(date));
              }}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 }
                }
              }}
            />
            <DatePicker
              label="Til"
              value={dayjs(props.dateTo, 'DD.MM.YYYY')}
              onChange={date => {
                if (date) props.setDateTo(formatDate(date));
              }}
              disableFuture
              slotProps={{
                textField: {
                  size: "small",
                  sx: { minWidth: 150 }
                }
              }}
            />
          </Stack>
        </Box >
        <Box sx={{ flexGrow: 1, height: '100%', minHeight: '400px' }}>
          <div ref={gridRef} style={{ height: '100%' }}>
            <DataGrid
              apiRef={props.apiRef}
              rows={props.transactions}
              columns={columns}
              loading={props.loading}
              density="standard"
              disableColumnFilter
              getRowHeight={() => 'auto'}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'id', sort: 'desc' }],
                },
              }}
              onRowClick={(params) => {
                handleRowClick(params.row);
              }}
              sx={{
                '& .MuiDataGrid-row:hover': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-paper': {
                  minWidth: 380,
                  maxWidth: 420,
                },
                '& .MuiDataGrid-filterForm': {
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  width: '100%',
                  gap: 2,
                },
              }}
              localeText={{
                toolbarFilters: "Síur",
                toolbarFiltersTooltipHide: "Fela síur",
                toolbarFiltersTooltipShow: "Sýna síur",
                columnHeaderFiltersLabel: "Sía eftir",
                filterOperatorContains: "Inniheldur",
                filterOperatorEquals: "Jafnt og",
                filterOperatorStartsWith: "Byrjar á",
                filterOperatorEndsWith: "Endar á",
                filterOperatorIs: "Er",
                filterOperatorNot: "Er ekki",
                filterOperatorIsEmpty: "Er tómt",
                filterOperatorIsNotEmpty: "Er ekki tómt",
                filterValueAny: "(einhver)",
                filterValueTrue: "satt",
                filterValueFalse: "ósatt",
                filterOperatorIsAnyOf: "Er einhver af",
                toolbarColumns: "Dálkar",
                toolbarColumnsLabel: "Dálkar",
                columnsPanelTextFieldLabel: "Finna dálk",
                columnsPanelTextFieldPlaceholder: "Leita að dálki",
                columnsPanelDragIconLabel: "Endurraða dálkum",
                columnsPanelShowAllButton: "Sýna alla",
                columnsPanelHideAllButton: "Fela alla",
                filterPanelColumns: "Dálkar",
                filterPanelOperator: "Skilyrði",
                filterPanelInputLabel: "Gildi",
                filterPanelInputPlaceholder: "Sía gildi",
                filterPanelAddFilter: "Bæta við síu",
                filterPanelDeleteIconLabel: "Eyða",
                columnMenuLabel: "Valmynd",
                columnMenuShowColumns: "Sýna dálka",
                columnMenuFilter: "Sía",
                columnMenuHideColumn: "Fela",
                columnMenuUnsort: "Hætta að raða",
                columnMenuSortAsc: "Raða hækkandi",
                columnMenuSortDesc: "Raða lækkandi",
                columnMenuManageColumns: "Stjórna dálkum",
              }}
            />
          </div>
        </Box>
      </Paper >
    </LocalizationProvider>
  );
};

export default TransactionsReport;
